const isConversationEvent = event => Boolean(
  event && event.id && event.message && typeof event.message === 'object'
)

const messageForEvent = event => isConversationEvent(event) ? event.message : null

const toolCalls = event => {
  const message = messageForEvent(event)
  return Array.isArray(message && message.tool_calls)
    ? message.tool_calls.filter(call => call && call.id)
    : []
}

const eventSequence = event => {
  const value = Number(event && event.sequence)
  return Number.isSafeInteger(value) ? value : 0
}

const visualGroupKey = event => {
  if (!isConversationEvent(event)) return event && event.id
  if (event.renderGroupId) return event.renderGroupId
  return event.id
}

const eventRenderEntry = event => event

const directConversationRenderIndex = events => {
  const ordered = (events || []).filter(Boolean).slice().sort((left, right) => (
    eventSequence(left) - eventSequence(right) ||
    String(left && left.id || '').localeCompare(String(right && right.id || ''))
  ))
  const callGroups = new Map()
  const callGroupByToolId = new Map()
  const resultsByToolId = new Map()

  for (const event of ordered) {
    if (!isConversationEvent(event)) continue
    const message = event.message
    const calls = toolCalls(event)
    if (calls.length) {
      const key = visualGroupKey(event)
      const group = callGroups.get(key) || []
      group.push(event)
      callGroups.set(key, group)
      for (const call of calls) callGroupByToolId.set(String(call.id), key)
    }
    if (message.tool_call_id) {
      const id = String(message.tool_call_id)
      const results = resultsByToolId.get(id) || []
      results.push(event)
      resultsByToolId.set(id, results)
    }
  }

  const representativeByEventId = new Map()
  const entriesByRepresentativeId = new Map()
  for (const [key, calls] of callGroups.entries()) {
    calls.sort((left, right) => eventSequence(left) - eventSequence(right))
    const representative = calls[0]
    const results = calls.flatMap(call => toolCalls(call).flatMap(toolCall => (
      resultsByToolId.get(String(toolCall.id)) || []
    )))
    const entries = [...calls, ...results]
      .sort((left, right) => eventSequence(left) - eventSequence(right))
      .map(eventRenderEntry)
    entriesByRepresentativeId.set(representative.id, entries)
    for (const event of [...calls, ...results]) {
      representativeByEventId.set(event.id, representative)
    }
    for (const call of calls) {
      for (const toolCall of toolCalls(call)) {
        callGroupByToolId.set(String(toolCall.id), key)
      }
    }
  }

  for (const event of ordered) {
    if (!isConversationEvent(event)) continue
    const message = event.message
    if (!message.tool_call_id || representativeByEventId.has(event.id)) continue
    entriesByRepresentativeId.set(event.id, [eventRenderEntry(event)])
    representativeByEventId.set(event.id, event)
  }

  const renderEvents = ordered.filter(event => {
    if (!isConversationEvent(event)) return true
    const representative = representativeByEventId.get(event.id)
    return !representative || representative.id === event.id
  })

  return {
    ordered,
    renderEvents,
    representativeByEventId,
    entriesByRepresentativeId
  }
}

const conversationEventsForRendering = events => (
  directConversationRenderIndex(events).renderEvents
)

const conversationEventForSourceEvent = (events, eventId) => {
  const index = directConversationRenderIndex(events)
  const exact = index.ordered.find(event => event && event.id === eventId)
  return exact && (index.representativeByEventId.get(exact.id) || exact) || null
}

const renderEntriesForConversationEvent = (events, event) => {
  if (!event) return []
  const index = directConversationRenderIndex(events)
  const representative = index.representativeByEventId.get(event.id) || event
  return index.entriesByRepresentativeId.get(representative.id) || []
}

const visibleConversationEvents = (events, activeEventId) => {
  const source = (events || []).filter(Boolean)
  const persistedIds = new Set(source
    .filter(isConversationEvent)
    .map(event => event.id))
  return source.filter(event => (
    isConversationEvent(event) ||
    !persistedIds.has(event.id) ||
    event.id === activeEventId
  ))
}

module.exports = {
  conversationEventForSourceEvent,
  conversationEventsForRendering,
  directConversationRenderIndex,
  eventRenderEntry,
  isConversationEvent,
  messageForEvent,
  renderEntriesForConversationEvent,
  visibleConversationEvents
}
